// src/components/Sidebar.js
import { NavLink, useNavigate } from "react-router-dom";
import {
  FaTachometerAlt,
  FaImages,
  FaSearch,
  FaMoneyBill,
  FaBell,
  FaCog,
  FaBars,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaSignOutAlt,
} from "react-icons/fa";
import React, { useEffect, useState } from "react";

const menu = [
  { name: "Dashboard",         icon: FaTachometerAlt, path: "/app" },
  { name: "Gallery",           icon: FaImages,        path: "/app/gallery" },
  { name: "Detection Gallery", icon: FaSearch,        path: "/app/detections-gallery" },
  { name: "Detections",        icon: FaSearch,        path: "/app/detections" },
  { name: "Costs",             icon: FaMoneyBill,     path: "/app/costs" },
  { name: "Alerts",            icon: FaBell,          path: "/app/alerts" },
  { name: "Setting",           icon: FaCog,           path: "/app/setting" },
];

export default function Sidebar() {
  const navigate = useNavigate();
  const [openMobile, setOpenMobile] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("sidebar_collapsed") === "1";
  });

  useEffect(() => {
    localStorage.setItem("sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  const user = { name: "Malcolm", email: "Malcolm@gmail.com", avatar: "" };

  function getInitials(name) {
    return name.split(" ").map((n) => n[0]).join("").toUpperCase();
  }

  function handleLogout() {
    localStorage.removeItem("auth");
    navigate("/login", { replace: true });
  }

  const MenuBlock = ({ onItemClick = () => {} }) => (
    <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
      {/* User profile */}
      <div
        className={`flex items-center gap-3 mb-5 cursor-pointer rounded-xl px-2 py-2.5
          hover:bg-white/5 transition-all duration-200
          ${collapsed ? "justify-center" : ""}`}
        onClick={() => { onItemClick(); navigate("/app/setting"); }}
      >
        <div className="relative flex-shrink-0">
          {user.avatar ? (
            <img src={user.avatar} alt="avatar"
              className="w-8 h-8 rounded-xl border-2 border-blue-500/40 object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600
              flex items-center justify-center text-white text-xs font-bold shadow">
              {getInitials(user.name)}
            </div>
          )}
          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400
            rounded-full border-2 border-slate-900" />
        </div>
        {!collapsed && (
          <div className="overflow-hidden min-w-0">
            <div className="text-sm font-semibold text-white truncate">{user.name}</div>
            <div className="text-xs text-slate-400 truncate">{user.email}</div>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="h-px bg-white/5 mb-3 mx-1" />

      {/* Nav */}
      <nav className="flex-1 flex flex-col gap-0.5">
        {menu.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.name}
              to={item.path}
              end={item.path === "/app"}
              onClick={onItemClick}
              title={collapsed ? item.name : undefined}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-2.5 py-2.5 rounded-xl transition-all duration-200
                 ${collapsed ? "justify-center" : ""}
                 ${isActive
                   ? "bg-blue-600 text-white shadow-lg shadow-blue-600/25"
                   : "text-slate-400 hover:bg-white/5 hover:text-slate-200"}`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className={`flex-shrink-0 text-sm transition-transform duration-200
                    ${isActive ? "" : "group-hover:scale-110"}`} />
                  {!collapsed && (
                    <span className="text-sm font-medium whitespace-nowrap">{item.name}</span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Divider */}
      <div className="h-px bg-white/5 mt-3 mb-2 mx-1" />

      {/* Logout */}
      {localStorage.getItem("auth") && (
        <button
          onClick={() => { onItemClick(); handleLogout(); }}
          title={collapsed ? "Logout" : undefined}
          className={`group flex items-center gap-3 px-2.5 py-2.5 rounded-xl
            text-slate-400 hover:bg-red-500/10 hover:text-red-400
            transition-all duration-200
            ${collapsed ? "justify-center" : ""}`}
        >
          <FaSignOutAlt className="flex-shrink-0 text-sm" />
          {!collapsed && <span className="text-sm font-medium">Logout</span>}
        </button>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-40 bg-slate-900 border-b border-white/5
        flex items-center justify-between px-4 py-3">
        <button
          onClick={() => setOpenMobile(true)}
          aria-label="Open menu"
          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 transition"
        >
          <FaBars size={16} />
        </button>
        <div className="text-lg font-bold">
          <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
            LucidX
          </span>
        </div>
        <div className="w-8" />
      </div>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 flex flex-col py-5 px-3
          transform transition-transform duration-300 md:hidden border-r border-white/5
          ${openMobile ? "translate-x-0" : "-translate-x-full"}`}
        role="dialog" aria-modal="true"
      >
        <div className="flex items-center justify-between px-1 mb-5">
          <div className="text-lg font-bold">
            <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
              LucidX
            </span>
          </div>
          <button onClick={() => setOpenMobile(false)} aria-label="Close menu"
            className="p-1.5 rounded-lg text-slate-400 hover:bg-white/5 hover:text-white transition">
            <FaTimes size={14} />
          </button>
        </div>
        <MenuBlock onItemClick={() => setOpenMobile(false)} />
      </div>

      {/* Mobile backdrop */}
      {openMobile && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm"
          onClick={() => setOpenMobile(false)} />
      )}

      {/* Desktop sidebar */}
      <div
        className={`hidden md:flex h-screen bg-slate-900 flex-col py-5 sticky top-0
          border-r border-white/5 transition-all duration-300 ease-in-out
          ${collapsed ? "w-[60px] px-2" : "w-56 px-3"}`}
      >
        {/* Brand + collapse */}
        <div className={`flex items-center mb-5 ${collapsed ? "justify-center" : "justify-between px-1"}`}>
          {!collapsed && (
            <div className="text-lg font-bold">
              <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
                LucidX
              </span>
            </div>
          )}
          <button
            onClick={() => setCollapsed((v) => !v)}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-white/5 hover:text-slate-300 transition"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <FaChevronRight size={11} /> : <FaChevronLeft size={11} />}
          </button>
        </div>

        <MenuBlock />
      </div>
    </>
  );
}
